/**
 * /crm/alertas — Listado de OTs con sub-estado activo o caducado.
 *
 * Las "alertas" son OTs que están en un sub-estado interno (esperando
 * repuestos, autorización, cliente, etc.) cuya fecha límite ya pasó o
 * está próxima. El admin las revisa, actualiza la fecha o limpia el
 * sub-estado para sacarlas del listado.
 */
import { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, Clock, ExternalLink, Bell, RefreshCw, Filter, Search, X,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import API from '@/lib/api';

const SUB_ESTADO_LABELS = {
  esperando_repuestos: 'Esperando repuestos',
  esperando_autorizacion: 'Esperando autorización',
  esperando_cliente: 'Esperando cliente',
  esperando_pago: 'Esperando pago',
  en_consulta_tecnica: 'En consulta técnica',
  pendiente_recogida: 'Pendiente recogida',
  aseguradora: 'Aseguradora',
  otro: 'Otro',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function Alertas() {
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ alertas: [], total: 0, caducados: 0, pendientes: 0 });
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState(() => searchParams.get('filtro') || 'todas');
  const [filtroSubEstado, setFiltroSubEstado] = useState(() => searchParams.get('subestado') || 'todos');
  const [busqueda, setBusqueda] = useState('');

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get('/alertas/sub-estados');
      setData(data);
    } catch (e) {
      toast.error('Error cargando alertas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const handleLimpiarSubEstado = async (ordenId, numero) => {
    if (!window.confirm(`¿Confirmas que quieres quitar el sub-estado de ${numero}? La OT dejará de aparecer en alertas.`)) return;
    try {
      // Usa el endpoint ya existente del CRM (poner subestado a "ninguno")
      await API.patch(`/ordenes/${ordenId}/subestado`, { subestado: 'ninguno' });
      toast.success('Sub-estado eliminado');
      cargar();
    } catch (e) {
      toast.error('No se pudo eliminar el sub-estado');
    }
  };

  const alertasFiltradas = data.alertas.filter((a) => {
    if (filtro === 'caducadas' && !a.caducado) return false;
    if (filtro === 'pendientes' && a.caducado) return false;
    if (filtroSubEstado !== 'todos' && a.subestado !== filtroSubEstado) return false;
    if (busqueda) {
      const q = busqueda.toLowerCase();
      const haystack = [
        a.numero_orden, a.cliente_nombre, a.dispositivo_modelo,
        a.numero_autorizacion, a.tecnico_nombre, a.motivo_subestado,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="alertas-page">
      {/* Cabecera */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Bell className="w-6 h-6 text-amber-500" />
            Alertas · Sub-estados
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Órdenes en sub-estado interno. Atiende las caducadas antes de que se pierdan en el flujo.
          </p>
        </div>
        <Button onClick={cargar} variant="outline" size="sm" disabled={loading} data-testid="alertas-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* Contadores */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card
          onClick={() => setFiltro('todas')}
          className={`cursor-pointer transition-colors ${filtro === 'todas' ? 'ring-2 ring-blue-500/60' : ''}`}
          data-testid="counter-total"
        >
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total alertas</p>
            <p className="text-3xl font-bold mt-1">{data.total}</p>
          </CardContent>
        </Card>
        <Card
          onClick={() => setFiltro('caducadas')}
          className={`cursor-pointer transition-colors ${filtro === 'caducadas' ? 'ring-2 ring-red-500/60' : ''}`}
          data-testid="counter-caducadas"
        >
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-red-500">Caducadas</p>
            <p className="text-3xl font-bold mt-1 text-red-600">{data.caducados}</p>
          </CardContent>
        </Card>
        <Card
          onClick={() => setFiltro('pendientes')}
          className={`cursor-pointer transition-colors ${filtro === 'pendientes' ? 'ring-2 ring-amber-500/60' : ''}`}
          data-testid="counter-pendientes"
        >
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wide text-amber-500">En curso (no caducadas)</p>
            <p className="text-3xl font-bold mt-1 text-amber-600">{data.pendientes}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filtroSubEstado} onValueChange={setFiltroSubEstado}>
              <SelectTrigger className="w-64" data-testid="filtro-sub-estado">
                <SelectValue placeholder="Sub-estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos los sub-estados</SelectItem>
                {Object.entries(SUB_ESTADO_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por OT, cliente, dispositivo, motivo…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="pl-9"
                data-testid="filtro-busqueda"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista */}
      <div className="space-y-2">
        {loading ? (
          <Card><CardContent className="p-8 text-center text-muted-foreground">Cargando…</CardContent></Card>
        ) : alertasFiltradas.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center text-muted-foreground">
              <Bell className="w-10 h-10 mx-auto mb-3 opacity-30" />
              No hay alertas en este filtro.
            </CardContent>
          </Card>
        ) : (
          alertasFiltradas.map((a) => (
            <AlertaRow key={a.orden_id} a={a} onLimpiar={() => handleLimpiarSubEstado(a.orden_id, a.numero_orden)} />
          ))
        )}
      </div>
    </div>
  );
}

function AlertaRow({ a, onLimpiar }) {
  const caducadoBadge = a.caducado ? (
    <Badge variant="destructive" className="gap-1" data-testid="badge-caducado">
      <AlertTriangle className="w-3 h-3" />
      Caducado · hace {Math.abs(a.dias_restantes ?? 0)} día(s)
    </Badge>
  ) : (
    <Badge className="bg-amber-500/15 text-amber-700 border-amber-500/30 gap-1">
      <Clock className="w-3 h-3" />
      Vence en {a.dias_restantes ?? '?'} día(s)
    </Badge>
  );

  return (
    <Card
      className={a.caducado ? 'border-red-500/40 bg-red-500/5' : 'border-amber-500/40 bg-amber-500/5'}
      data-testid={`alerta-row-${a.orden_id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="space-y-1 flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 flex-wrap">
              <Link
                to={`/crm/ordenes/${a.orden_id}`}
                className="font-mono font-semibold hover:underline"
                data-testid={`link-orden-${a.orden_id}`}
              >
                {a.numero_orden}
              </Link>
              {caducadoBadge}
              <Badge variant="outline" className="text-xs">
                {SUB_ESTADO_LABELS[a.subestado] || a.subestado}
              </Badge>
              <Badge variant="outline" className="text-xs capitalize">
                {(a.estado || '').replace(/_/g, ' ')}
              </Badge>
            </div>
            <div className="text-sm">
              <strong>{a.cliente_nombre || 'Sin cliente'}</strong>
              {a.dispositivo_modelo && <> · {a.dispositivo_modelo}</>}
              {a.tecnico_nombre && <> · Técnico: {a.tecnico_nombre}</>}
            </div>
            {a.motivo_subestado && (
              <p className="text-sm text-muted-foreground italic">
                "{a.motivo_subestado}"
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Fecha revisión: <strong>{formatDate(a.fecha_revision_subestado)}</strong>
              {a.ultimo_cambio?.usuario && <> · Cambiado por: {a.ultimo_cambio.usuario}</>}
              {a.ultimo_cambio?.fecha && <> · {formatDate(a.ultimo_cambio.fecha)}</>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild size="sm" variant="default" data-testid={`btn-revisar-${a.orden_id}`}>
              <Link to={`/crm/ordenes/${a.orden_id}`}>
                <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                Revisar OT
              </Link>
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={onLimpiar}
              data-testid={`btn-limpiar-${a.orden_id}`}
            >
              <X className="w-3.5 h-3.5 mr-1.5" />
              Quitar sub-estado
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
